import { createContext, useContext, useState, ReactNode, FC } from "react";
import { useOnMount } from "@hooks/useOnMount";

export const WEBVIEW_MESSAGE_TYPE = {
  LOG: "log",
  EXTERNAL_LINK: "external-link",
} as const;

export type WebViewMessageType =
  (typeof WEBVIEW_MESSAGE_TYPE)[keyof typeof WEBVIEW_MESSAGE_TYPE];

type WebViewContextData = {
  isWebView: boolean;
  setIsWebView: (value: boolean) => void;
  sendWebViewMessage: (
    type: WebViewMessageType,
    payload: Record<string, unknown>,
  ) => void;
};

export const WebViewContext = createContext<WebViewContextData>({
  isWebView: false,
  setIsWebView: () => {},
  sendWebViewMessage: () => {},
});

interface WebViewProviderProps {
  children: ReactNode;
}

export const WebViewProvider: FC<WebViewProviderProps> = ({ children }) => {
  const [isWebView, setIsWebView] = useState(false);

  useOnMount(() => {
    // Persistence of the webview state across page reloads
    if (typeof window !== "undefined") {
      const storedValue = localStorage.getItem("isWebView");
      handleSetIsWebView(storedValue ? JSON.parse(storedValue) : false);
    }
  });

  const handleSetIsWebView = (value: boolean) => {
    setIsWebView(value);
    localStorage.setItem("isWebView", JSON.stringify(value));
  };

  const sendWebViewMessage = (
    type: WebViewMessageType,
    payload: Record<string, unknown>,
  ) => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type, ...payload }),
      );
    } else {
      console.warn("ReactNativeWebView is not available");
    }
  };

  return (
    <WebViewContext.Provider
      value={{
        isWebView,
        setIsWebView: handleSetIsWebView,
        sendWebViewMessage,
      }}
    >
      {children}
    </WebViewContext.Provider>
  );
};

export const useWebViewContext = () => useContext(WebViewContext);
